import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowRight01Icon, Loading02Icon } from '@hugeicons/core-free-icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/context/AuthContext';
import { api, apiError } from '@/lib/api';
import { ROLE_LABEL } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ViaBrandLockup } from '@/components/brand/InstitutionalBrand';
import { AUTH_MODE, AZURE_AD_CONFIG } from '@/config';

const HERO_IMG =
  'https://images.unsplash.com/photo-1600851555921-d280ba3f019a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob3NwaXRhbCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODM4Mjc2MDJ8MA&ixlib=rb-4.1.0&q=85';

/**
 * Determines if we're in demo mode (profile selector) or credential mode (email/password + SSO).
 */
function isDemoMode() {
  return AUTH_MODE === 'demo';
}

// ─── PKCE Utilities ──────────────────────────────────────────────────────────

/**
 * Generate a cryptographically random code_verifier (43–128 characters, URL-safe).
 */
function generateCodeVerifier() {
  const array = new Uint8Array(64);
  crypto.getRandomValues(array);
  return base64urlEncode(array);
}

/**
 * Derive code_challenge = base64url(SHA-256(code_verifier)).
 */
async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64urlEncode(new Uint8Array(digest));
}

/**
 * Generate a random state value for CSRF protection.
 */
function generateState() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64urlEncode(array);
}

/**
 * Base64url encoding (RFC 7636) without padding.
 */
function base64urlEncode(buffer) {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const isDemo = isDemoMode();

  // Demo mode state
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);

  // Credential mode state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isDemo) return;
    api
      .get('/auth/users')
      .then(({ data }) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => toast.error('No fue posible cargar los usuarios'));
  }, [isDemo]);

  // ─── Demo login handler ─────────────────────────────────────────────────────
  const handleDemoLogin = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await login(selected.email);
      navigate('/');
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // ─── Credential login handler ───────────────────────────────────────────────
  const handleCredentialLogin = async (e) => {
    e.preventDefault();
    const validationErrors = {};

    if (!email.trim()) {
      validationErrors.email = 'El correo electrónico es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      validationErrors.email = 'Ingrese un correo electrónico válido';
    }

    if (!password) {
      validationErrors.password = 'La contraseña es obligatoria';
    } else if (password.length < 8) {
      validationErrors.password = 'La contraseña debe tener al menos 8 caracteres';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // ─── Microsoft SSO redirect ─────────────────────────────────────────────────
  const handleMicrosoftLogin = async () => {
    try {
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateState();

      // Store PKCE values in sessionStorage for the callback to use
      sessionStorage.setItem('via_pkce_code_verifier', codeVerifier);
      sessionStorage.setItem('via_pkce_state', state);

      const { tenantId, clientId, redirectUri } = AZURE_AD_CONFIG;
      const params = new URLSearchParams({
        client_id: clientId,
        response_type: 'code',
        redirect_uri: redirectUri,
        scope: 'openid profile email',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state,
      });

      const authorizeUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
      window.location.href = authorizeUrl;
    } catch (err) {
      toast.error('Error al iniciar autenticación con Microsoft');
      console.error('Microsoft SSO redirect error:', err);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel — Hero with background photo (desktop only) */}
      <div className="relative hidden lg:flex flex-col overflow-hidden">
        {/* Background image */}
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center"
          aria-hidden="true"
        />
        {/* Institutional overlay — primary #671E75, secondary #307FE2 */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(150deg, rgba(50, 15, 58, 0.93) 0%, rgba(103, 30, 117, 0.88) 52%, rgba(40, 90, 170, 0.85) 100%)',
          }}
        />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          {/* Logo */}
          <ViaBrandLockup contrast="light" />

          {/* Main headline */}
          <div className="max-w-md">
            <h1 className="font-display text-4xl font-semibold text-white leading-tight">
              VIA: gobierno y adopción responsable de inteligencia artificial
            </h1>
            <p className="mt-4 text-white/90 leading-relaxed text-sm">
              Detecte, registre, evalúe, decida, monitoree y reevalúe todo el ecosistema de IA de la organización con
              trazabilidad completa.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {['AI Registry', 'Shadow AI', 'Risk Assessment', 'Comité de IA', 'Auditoría'].map((t) => (
                <span
                  key={t}
                  className="px-3 py-1 rounded-full border border-white/30 text-white/90 text-xs font-medium bg-white/10 backdrop-blur">
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Stats bar inspired by Stripe — with animated wave accent */}
          <div className="relative">
            <StatsWave />
            <div className="relative z-10 grid grid-cols-3 gap-4 pt-6">
              {[
                { value: '100%', label: 'Trazabilidad de decisiones' },
                { value: '6', label: 'Ejes de gobernanza' },
                { value: '24/7', label: 'Monitoreo continuo' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="font-display text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-[11px] text-white/70 mt-0.5 leading-tight">{stat.label}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-xs text-white/65">UC CHRISTUS · Dirección de Innovación y Gobernanza de IA</p>
          </div>
        </div>
      </div>

      {/* Right panel — Login form (desktop: light on white) */}
      <div className="light hidden lg:flex items-center justify-center p-6 sm:p-12 bg-white text-foreground">
        <div className="w-full max-w-md animate-in-up">
          <h2 className="font-display text-2xl font-semibold text-gray-900">Acceso a la plataforma</h2>
          <p className="text-sm text-gray-500 mt-1.5">
            {isDemo
              ? 'Ambiente de demostración · seleccione un perfil institucional para continuar.'
              : 'Ingrese sus credenciales institucionales o use Microsoft Entra.'}
          </p>

          {isDemo ? (
            <DemoProfileSelector
              users={users}
              selected={selected}
              onSelect={setSelected}
              loading={loading}
              onLogin={handleDemoLogin}
              onMicrosoftLogin={handleMicrosoftLogin}
            />
          ) : (
            <CredentialLoginForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              errors={errors}
              loading={loading}
              onSubmit={handleCredentialLogin}
              onMicrosoftLogin={handleMicrosoftLogin}
            />
          )}

          <p className="text-[11px] text-gray-400 text-center mt-4">
            Acceso auditado · cada inicio de sesión queda registrado en la bitácora institucional
          </p>
        </div>
      </div>

      {/* ─── Mobile Layout ─── Full-screen immersive login ──────────────────── */}
      <div className="lg:hidden flex flex-col min-h-screen relative overflow-hidden">
        {/* Animated gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(160deg, #1a0520 0%, #3d1046 25%, #671E75 50%, #2E5AA8 80%, #0D1B2A 100%)',
          }}
          aria-hidden="true"
        />

        {/* Decorative orbs */}
        <div
          className="absolute top-[-15%] right-[-20%] w-[70vw] h-[70vw] rounded-full opacity-30 blur-3xl"
          style={{ background: 'radial-gradient(circle, #9B4CA6 0%, transparent 70%)' }}
          aria-hidden="true"
        />
        <div
          className="absolute bottom-[-10%] left-[-15%] w-[60vw] h-[60vw] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, #307FE2 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        {/* Subtle mesh pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
          aria-hidden="true"
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col flex-1 px-6 pt-12 pb-8">
          {/* Header: Brand */}
          <div className="flex flex-col items-center text-center mb-8 animate-in-up">
            <ViaBrandLockup contrast="light" />
            <p className="mt-4 text-white/70 text-xs font-medium tracking-wide uppercase">
              Gobernanza de Inteligencia Artificial
            </p>
          </div>

          {/* Glassmorphism card */}
          <div
            className="flex-1 flex flex-col rounded-3xl p-6 animate-in-up"
            style={{
              background: 'rgba(255, 255, 255, 0.07)',
              backdropFilter: 'blur(20px) saturate(1.4)',
              WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            }}>
            <h2 className="font-display text-xl font-semibold text-white text-center">Acceso a la plataforma</h2>
            <p className="text-sm text-white/60 text-center mt-1.5 mb-6">
              {isDemo ? 'Seleccione un perfil institucional' : 'Credenciales institucionales o Microsoft Entra'}
            </p>

            <div className="flex-1 flex flex-col justify-center">
              {isDemo ? (
                <MobileDemoProfileSelector
                  users={users}
                  selected={selected}
                  onSelect={setSelected}
                  loading={loading}
                  onLogin={handleDemoLogin}
                  onMicrosoftLogin={handleMicrosoftLogin}
                />
              ) : (
                <MobileCredentialLoginForm
                  email={email}
                  setEmail={setEmail}
                  password={password}
                  setPassword={setPassword}
                  errors={errors}
                  loading={loading}
                  onSubmit={handleCredentialLogin}
                  onMicrosoftLogin={handleMicrosoftLogin}
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <p className="text-[10px] text-white/40 text-center mt-5">Acceso auditado · bitácora institucional</p>
        </div>
      </div>
    </div>
  );
}

// ─── Credential Login Form ───────────────────────────────────────────────────

function CredentialLoginForm({ email, setEmail, password, setPassword, errors, loading, onSubmit, onMicrosoftLogin }) {
  return (
    <div className="mt-8">
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4" aria-label="Formulario de inicio de sesión">
        <div className="space-y-1.5">
          <Label htmlFor="login-email">Correo electrónico</Label>
          <Input
            id="login-email"
            type="email"
            placeholder="nombre@ucchristus.cl"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'login-email-error' : undefined}
            className={cn('h-11 rounded-xl', errors.email && 'border-red-500 focus-visible:ring-red-500')}
            data-testid="login-email-input"
          />
          {errors.email && (
            <p id="login-email-error" className="text-xs text-red-600" role="alert">
              {errors.email}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="login-password">Contraseña</Label>
          <Input
            id="login-password"
            type="password"
            placeholder="Mínimo 8 caracteres"
            required
            minLength={8}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'login-password-error' : undefined}
            className={cn('h-11 rounded-xl', errors.password && 'border-red-500 focus-visible:ring-red-500')}
            data-testid="login-password-input"
          />
          {errors.password && (
            <p id="login-password-error" className="text-xs text-red-600" role="alert">
              {errors.password}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="mt-2 h-11 w-full rounded-xl font-medium bg-[#307FE2] text-white hover:bg-[#2968c0] shadow-lg shadow-[#307FE2]/30 border-0 transition-all duration-200"
          data-testid="login-submit-btn">
          {loading ? (
            <HugeiconsIcon icon={Loading02Icon} className="animate-spin" aria-label="Cargando" />
          ) : (
            <>
              Iniciar sesión
              <HugeiconsIcon icon={ArrowRight01Icon} data-icon="inline-end" aria-hidden="true" />
            </>
          )}
        </Button>
      </form>

      {/* Divider */}
      <div className="relative my-6" aria-hidden="true">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-3 text-gray-400">o</span>
        </div>
      </div>

      {/* Microsoft SSO button */}
      <Button
        type="button"
        variant="outline"
        onClick={onMicrosoftLogin}
        disabled={loading}
        className="h-11 w-full rounded-xl font-medium border-gray-300 bg-[#2F2F2F] text-white hover:bg-[#1a1a1a] hover:text-white transition-all duration-200"
        data-testid="login-microsoft-btn">
        <MicrosoftLogo className="mr-2 size-5 shrink-0" aria-hidden="true" />
        Ingresar con Microsoft
      </Button>
    </div>
  );
}

// ─── Demo Profile Selector (backward compatible) ─────────────────────────────

function DemoProfileSelector({ users, selected, onSelect, loading, onLogin, onMicrosoftLogin }) {
  return (
    <>
      <div className="mt-8 flex flex-col gap-2" role="radiogroup" aria-label="Seleccionar perfil de acceso">
        {users.length === 0 && (
          <p className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
            Aún no hay perfiles institucionales habilitados en este ambiente.
          </p>
        )}
        {users.map((u) => (
          <button
            key={u.id}
            onClick={() => onSelect(u)}
            role="radio"
            aria-checked={selected?.id === u.id}
            data-testid={`login-user-${u.role}${u.id === 'u-com2' || u.id === 'u-com3' ? '-' + u.id : ''}`}
            className={cn(
              'w-full flex items-center gap-4 rounded-xl border p-3.5 text-left transition-all duration-200',
              selected?.id === u.id
                ? 'border-[#307FE2] bg-[#307FE2]/5 shadow-md ring-1 ring-[#307FE2]/20'
                : 'border-gray-200 bg-white hover:border-[#307FE2]/40 hover:shadow-sm',
            )}>
            <Avatar className="size-10">
              <AvatarFallback
                className={cn(
                  'text-xs font-bold',
                  selected?.id === u.id ? 'bg-[#307FE2] text-white' : 'bg-[#307FE2] text-white',
                )}>
                {u.initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">{u.name}</p>
              <p className="text-xs text-gray-500 truncate">{u.title}</p>
            </div>
            <Badge variant="outline" className="shrink-0 border-gray-300 text-[#307FE2] bg-transparent text-[11px]">
              {ROLE_LABEL[u.role]}
            </Badge>
          </button>
        ))}
      </div>

      <Button
        className={cn(
          'mt-6 h-11 w-full rounded-xl font-medium border-0 transition-all duration-200',
          selected
            ? 'bg-[#307FE2] text-white hover:bg-[#2968c0] shadow-lg shadow-[#307FE2]/30'
            : 'bg-[#307FE2]/20 text-[#307FE2] cursor-not-allowed',
        )}
        disabled={!selected || loading}
        onClick={onLogin}
        data-testid="login-submit-btn">
        {loading ? (
          <HugeiconsIcon icon={Loading02Icon} className="animate-spin" aria-label="Cargando" />
        ) : (
          <>
            Ingresar a la plataforma <HugeiconsIcon icon={ArrowRight01Icon} data-icon="inline-end" aria-hidden="true" />
          </>
        )}
      </Button>

      {/* Divider */}
      <div className="relative my-6" aria-hidden="true">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-3 text-gray-400">o inicia sesión con tu cuenta corporativa</span>
        </div>
      </div>

      {/* Microsoft SSO button for new users */}
      <Button
        type="button"
        variant="outline"
        onClick={onMicrosoftLogin}
        disabled={loading}
        className="h-11 w-full rounded-xl font-medium border-gray-300 bg-[#2F2F2F] text-white hover:bg-[#1a1a1a] hover:text-white transition-all duration-200"
        data-testid="login-microsoft-btn">
        <MicrosoftLogo className="mr-2 size-5 shrink-0" aria-hidden="true" />
        Ingresar con Microsoft
      </Button>

      <p className="text-[11px] text-gray-500 text-center mt-3">
        ¿Primera vez? Al ingresar con Microsoft se creará tu cuenta con acceso básico.
        <br />
        Un administrador podrá asignarte permisos adicionales.
      </p>
    </>
  );
}

// ─── Mobile Credential Login Form ────────────────────────────────────────────

function MobileCredentialLoginForm({
  email,
  setEmail,
  password,
  setPassword,
  errors,
  loading,
  onSubmit,
  onMicrosoftLogin,
}) {
  return (
    <div>
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4"
        aria-label="Formulario de inicio de sesión móvil">
        <div className="space-y-1.5">
          <Label htmlFor="mobile-login-email" className="text-white/80 text-xs font-medium">
            Correo electrónico
          </Label>
          <Input
            id="mobile-login-email"
            type="email"
            placeholder="nombre@ucchristus.cl"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'mobile-login-email-error' : undefined}
            className={cn(
              'h-12 rounded-xl bg-white/10 border-white/15 text-white placeholder:text-white/40 focus:border-[#307FE2] focus:ring-[#307FE2]/30',
              errors.email && 'border-red-400 focus-visible:ring-red-400/30',
            )}
          />
          {errors.email && (
            <p id="mobile-login-email-error" className="text-xs text-red-300" role="alert">
              {errors.email}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="mobile-login-password" className="text-white/80 text-xs font-medium">
            Contraseña
          </Label>
          <Input
            id="mobile-login-password"
            type="password"
            placeholder="Mínimo 8 caracteres"
            required
            minLength={8}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'mobile-login-password-error' : undefined}
            className={cn(
              'h-12 rounded-xl bg-white/10 border-white/15 text-white placeholder:text-white/40 focus:border-[#307FE2] focus:ring-[#307FE2]/30',
              errors.password && 'border-red-400 focus-visible:ring-red-400/30',
            )}
          />
          {errors.password && (
            <p id="mobile-login-password-error" className="text-xs text-red-300" role="alert">
              {errors.password}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="mt-3 h-12 w-full rounded-xl font-semibold text-white border-0 transition-all duration-300"
          style={{
            background: 'linear-gradient(135deg, #307FE2 0%, #5823C9 100%)',
            boxShadow: '0 4px 20px rgba(48, 127, 226, 0.4)',
          }}>
          {loading ? (
            <HugeiconsIcon icon={Loading02Icon} className="animate-spin" aria-label="Cargando" />
          ) : (
            <>
              Iniciar sesión
              <HugeiconsIcon icon={ArrowRight01Icon} data-icon="inline-end" aria-hidden="true" />
            </>
          )}
        </Button>
      </form>

      {/* Divider */}
      <div className="relative my-5" aria-hidden="true">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-white/15" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-3 text-white/40" style={{ background: 'rgba(255,255,255,0.07)' }}>
            o
          </span>
        </div>
      </div>

      {/* Microsoft SSO button */}
      <Button
        type="button"
        variant="outline"
        onClick={onMicrosoftLogin}
        disabled={loading}
        className="h-12 w-full rounded-xl font-medium border-white/20 bg-white/10 text-white hover:bg-white/15 hover:text-white transition-all duration-200 backdrop-blur">
        <MicrosoftLogo className="mr-2 size-5 shrink-0" aria-hidden="true" />
        Ingresar con Microsoft
      </Button>
    </div>
  );
}

// ─── Mobile Demo Profile Selector ────────────────────────────────────────────

function MobileDemoProfileSelector({ users, selected, onSelect, loading, onLogin, onMicrosoftLogin }) {
  return (
    <>
      <div
        className="flex flex-col gap-2 max-h-[40vh] overflow-y-auto"
        role="radiogroup"
        aria-label="Seleccionar perfil de acceso">
        {users.length === 0 && (
          <p className="rounded-xl border border-dashed border-white/20 p-4 text-sm text-white/50">
            Aún no hay perfiles habilitados en este ambiente.
          </p>
        )}
        {users.map((u) => (
          <button
            key={u.id}
            onClick={() => onSelect(u)}
            role="radio"
            aria-checked={selected?.id === u.id}
            className={cn(
              'w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-all duration-200',
              selected?.id === u.id
                ? 'border-[#307FE2]/60 bg-[#307FE2]/15 shadow-lg shadow-[#307FE2]/10'
                : 'border-white/12 bg-white/5 hover:border-white/25 hover:bg-white/8',
            )}>
            <Avatar className="size-9">
              <AvatarFallback className="text-xs font-bold bg-[#307FE2] text-white">{u.initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white truncate">{u.name}</p>
              <p className="text-xs text-white/50 truncate">{u.title}</p>
            </div>
            <Badge variant="outline" className="shrink-0 border-white/20 text-white/80 bg-transparent text-[10px]">
              {ROLE_LABEL[u.role]}
            </Badge>
          </button>
        ))}
      </div>

      <Button
        className={cn(
          'mt-5 h-12 w-full rounded-xl font-semibold border-0 transition-all duration-300',
          selected ? 'text-white' : 'bg-white/10 text-white/40 cursor-not-allowed',
        )}
        style={
          selected
            ? {
                background: 'linear-gradient(135deg, #307FE2 0%, #5823C9 100%)',
                boxShadow: '0 4px 20px rgba(48, 127, 226, 0.4)',
              }
            : undefined
        }
        disabled={!selected || loading}
        onClick={onLogin}>
        {loading ? (
          <HugeiconsIcon icon={Loading02Icon} className="animate-spin" aria-label="Cargando" />
        ) : (
          <>
            Ingresar a la plataforma
            <HugeiconsIcon icon={ArrowRight01Icon} data-icon="inline-end" aria-hidden="true" />
          </>
        )}
      </Button>

      {/* Divider */}
      <div className="relative my-5" aria-hidden="true">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-white/15" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-3 text-white/40" style={{ background: 'rgba(255,255,255,0.07)' }}>
            o cuenta corporativa
          </span>
        </div>
      </div>

      {/* Microsoft SSO button */}
      <Button
        type="button"
        variant="outline"
        onClick={onMicrosoftLogin}
        disabled={loading}
        className="h-12 w-full rounded-xl font-medium border-white/20 bg-white/10 text-white hover:bg-white/15 hover:text-white transition-all duration-200 backdrop-blur">
        <MicrosoftLogo className="mr-2 size-5 shrink-0" aria-hidden="true" />
        Ingresar con Microsoft
      </Button>

      <p className="text-[10px] text-white/40 text-center mt-3">
        ¿Primera vez? Al ingresar con Microsoft se creará tu cuenta con acceso básico.
      </p>
    </>
  );
}

// ─── Microsoft Logo SVG ──────────────────────────────────────────────────────

function MicrosoftLogo({ className }) {
  return (
    <svg className={className} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

// ─── Stats Wave Animation ────────────────────────────────────────────────────

/**
 * Animated wave lines for the stats section — inspired by Stripe's aesthetic.
 * Pure SVG with CSS animation, minimal CPU. Complements the photo background.
 */
function StatsWave() {
  const prefersReduced =
    typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;

  return (
    <div className="absolute inset-x-0 -top-8 h-20 opacity-60 pointer-events-none" aria-hidden="true">
      <svg viewBox="0 0 600 80" className="w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="stat-wave-1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(263, 83%, 76%)" stopOpacity="0.8" />
            <stop offset="50%" stopColor="hsl(292, 70%, 60%)" stopOpacity="0.9" />
            <stop offset="100%" stopColor="hsl(195, 100%, 67%)" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="stat-wave-2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(195, 100%, 67%)" stopOpacity="0.5" />
            <stop offset="50%" stopColor="hsl(166, 76%, 68%)" stopOpacity="0.7" />
            <stop offset="100%" stopColor="hsl(263, 83%, 76%)" stopOpacity="0.4" />
          </linearGradient>
        </defs>

        <path
          d="M0,50 Q100,30 200,50 T400,45 T600,50"
          fill="none"
          stroke="url(#stat-wave-1)"
          strokeWidth="2"
          strokeLinecap="round">
          {!prefersReduced && (
            <animate
              attributeName="d"
              values="M0,50 Q100,30 200,50 T400,45 T600,50;M0,45 Q100,60 200,40 T400,55 T600,45;M0,50 Q100,30 200,50 T400,45 T600,50"
              dur="8s"
              repeatCount="indefinite"
            />
          )}
        </path>
        <path
          d="M0,55 Q150,40 300,58 T600,52"
          fill="none"
          stroke="url(#stat-wave-2)"
          strokeWidth="1.5"
          strokeLinecap="round">
          {!prefersReduced && (
            <animate
              attributeName="d"
              values="M0,55 Q150,40 300,58 T600,52;M0,52 Q150,65 300,45 T600,58;M0,55 Q150,40 300,58 T600,52"
              dur="10s"
              repeatCount="indefinite"
            />
          )}
        </path>
        <path
          d="M0,60 Q200,50 400,62 T600,58"
          fill="none"
          stroke="url(#stat-wave-1)"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.5">
          {!prefersReduced && (
            <animate
              attributeName="d"
              values="M0,60 Q200,50 400,62 T600,58;M0,58 Q200,68 400,52 T600,62;M0,60 Q200,50 400,62 T600,58"
              dur="12s"
              repeatCount="indefinite"
            />
          )}
        </path>
      </svg>
    </div>
  );
}
